import React from 'react';

/** Presentation-only baseline view for the regional forecasting workspace. */
const RegionalBaselineStage = ({ vm }) => {
  const { Bar, CartesianGrid, ComposedChart, Database, ForecastGrowthMatrix, ForecastReadinessBadge, ForecastTimeMatrix, History, Legend, Line, ResponsiveContainer, Tooltip, WORKSPACE_READINESS, XAxis, YAxis, activeWorkspaceStage, baseForecastRows, baselineAnnualSeries, baselineSource, canEditSubmission, chartAxisColor, chartGridColor, formatMillions, growthMatrixColumns, op01AnnualSeries, op01CurrentYearRow, op01MonthlySeries, setWorkspaceState, tooltipStyle, workspaceState } = vm;

  return (
    <>
{activeWorkspaceStage === 'baseline' && <section className="forecast-panel forecast-journey-section workspace-stage-panel" id="regional-stage-baseline" tabIndex="-1">
        <details className="forecast-accordion" open>
          <summary>
            <div>
              <span className="forecast-section-code">OP_01 · Base forecast</span>
              <h2>Existing-state executive review before market input</h2>
            </div>
            <Database size={18} />
          </summary>

          <div className="forecast-accordion-body">
            <div className={`forecast-load-message ${baselineSource.tone === 'moss' ? 'success' : ''}`}>
              <Database size={17} />
              <div>
                <strong>{baselineSource.status}</strong>
                <small>{baselineSource.message}</small>
              </div>
            </div>

            {!workspaceState.baselineDecision && canEditSubmission && (
              <div className="forecast-load-message" role="note">
                <History size={17} />
                <div>
                  <strong>One decision completes this stage</strong>
                  <small>
                    Confirm the baseline is fit to plan from, or flag a data issue at the bottom of
                    this page. Nothing else on this stage is required.
                  </small>
                </div>
                <div className="workspace-action-row" style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
                  <button
                    type="button"
                    className="forecast-primary-button compact"
                    onClick={() => setWorkspaceState((previous) => ({
                      ...previous, baselineDecision: 'confirmed', baselineReviewedAt: new Date().toISOString(), baselineIssue: ''
                    }))}
                  >
                    Confirm baseline
                  </button>
                  <button
                    type="button"
                    className="forecast-secondary-button compact"
                    onClick={() => document.getElementById('baseline-review-decision')?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
                  >
                    Jump to decision
                  </button>
                </div>
              </div>
            )}

            <div className="forecast-narrative-grid">
              <div>
                <span>{op01CurrentYearRow?.year || 'Current year'} net income</span>
                <strong>{formatMillions(op01CurrentYearRow?.netIncome || baselineAnnualSeries[0].netIncome)}</strong>
                <small>{op01CurrentYearRow?.subLabel || 'No producer hiring or discretionary market overrides.'}</small>
              </div>
              <div>
                <span>FY2027 base NII</span>
                <strong>{formatMillions(baselineAnnualSeries[0].nii)}</strong>
                <small>First full forecast year, including base SOFR and deposit beta.</small>
              </div>
              <div>
                <span>FY2031 base loans</span>
                <strong>{formatMillions(baselineAnnualSeries[4].endingLoans, 0)}</strong>
                <small>Ending balance from 60-month portfolio engine.</small>
              </div>
              <div>
                <span>FY2031 base deposits</span>
                <strong>{formatMillions(baselineAnnualSeries[4].endingDeposits, 0)}</strong>
                <small>Core deposit growth net of base runoff assumptions.</small>
              </div>
            </div>

            {op01CurrentYearRow?.sourceType === 'mixed' && (
              <div className="forecast-submit-note forecast-actuals-note">
                OP_01 now includes prior full-year actuals, {op01CurrentYearRow.actualMonths} month(s) of current-year actuals,
                and {op01CurrentYearRow.forecastMonths} month(s) of remaining current-year forecast before the 60-month plan horizon.
              </div>
            )}

            <div className="forecast-base-review-grid">
              <div className="forecast-panel-lite">
                <div className="forecast-panel-head">
                  <div>
                    <span className="forecast-section-code">Base balance path</span>
                    <h3>Monthly loans, deposits, and NII</h3>
                  </div>
                  <div className="forecast-submit-chips forecast-source-chips">
                    <span className="forecast-health-chip neutral">Actuals</span>
                    <span className="forecast-health-chip gold">Current-year forecast</span>
                    <span className="forecast-health-chip green">60-month plan</span>
                  </div>
                </div>
                <div className="forecast-chart forecast-wide-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={op01MonthlySeries} margin={{ top: 12, right: 16, left: 0, bottom: 12 }}>
                      <CartesianGrid stroke={chartGridColor} vertical={false} strokeDasharray="3 5" />
                      <XAxis dataKey="period" tick={{ fill: chartAxisColor, fontSize: 10 }} interval={5} />
                      <YAxis yAxisId="left" tick={{ fill: chartAxisColor, fontSize: 10 }} />
                      <YAxis yAxisId="right" orientation="right" tick={{ fill: chartAxisColor, fontSize: 10 }} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(value) => formatMillions(value)} />
                      <Legend wrapperStyle={{ color: chartAxisColor, fontSize: 11 }} />
                      <Line yAxisId="left" type="monotone" dataKey="endingLoans" name="Ending loans" stroke="hsl(var(--chart-3))" strokeWidth={2.4} dot={false} />
                      <Line yAxisId="left" type="monotone" dataKey="endingDeposits" name="Ending deposits" stroke="hsl(var(--chart-2))" strokeWidth={2.4} dot={false} />
                      <Line yAxisId="right" type="monotone" dataKey="nii" name="Monthly NII" stroke="hsl(var(--chart-1))" strokeWidth={2.4} strokeDasharray="6 4" dot={false} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
                <p className="forecast-submit-note forecast-basis-note">
                  NII actuals are GL as-reported; the forward line is GL-calibrated to trailing loan yield,
                  deposit cost, and residual funding-benefit credit. This is not a GAAP-vs-FTP basis switch.
                </p>
              </div>

              <div className="forecast-panel-lite">
                <div className="forecast-panel-head">
                  <div>
                    <span className="forecast-section-code">Base P&L bridge</span>
                    <h3>Annual operating line items</h3>
                  </div>
                </div>
                <div className="forecast-chart forecast-wide-chart">
                  <ResponsiveContainer width="100%" height="100%">
                    <ComposedChart data={op01AnnualSeries} margin={{ top: 12, right: 16, left: 0, bottom: 12 }}>
                      <CartesianGrid stroke={chartGridColor} vertical={false} strokeDasharray="3 5" />
                      <XAxis dataKey="year" tick={{ fill: chartAxisColor, fontSize: 11 }} />
                      <YAxis tick={{ fill: chartAxisColor, fontSize: 10 }} />
                      <Tooltip contentStyle={tooltipStyle} formatter={(value) => formatMillions(value)} />
                      <Legend wrapperStyle={{ color: chartAxisColor, fontSize: 11 }} />
                      <Bar dataKey="nii" name="NII" fill="hsl(var(--chart-2))" radius={[5, 5, 0, 0]} />
                      <Bar dataKey="nonInterestIncome" name="Fees" fill="hsl(var(--chart-1))" radius={[5, 5, 0, 0]} />
                      <Bar dataKey="nonInterestExpense" name="NIE" fill="hsl(var(--chart-4))" radius={[5, 5, 0, 0]} />
                      <Line type="monotone" dataKey="netIncome" name="Net income" stroke="hsl(var(--chart-3))" strokeWidth={2.8} />
                    </ComposedChart>
                  </ResponsiveContainer>
                </div>
              </div>
            </div>

            <details className="workspace-change-drawer">
              <summary>
                <History size={15} aria-hidden="true" /> Detailed baseline schedules and calculation matrices
              </summary>
              <div className="workspace-change-content">
                <ForecastTimeMatrix monthlySeries={op01MonthlySeries} />

                {growthMatrixColumns.length > 2 && (
                  <ForecastGrowthMatrix
                    title="OP_01G · Baseline growth path"
                    columns={growthMatrixColumns}
                  />
                )}

                <div className="forecast-table-shell ace-scroll-shell forecast-compact-table">
                  <table className="forecast-table forecast-comparison-table">
                <thead>
                  <tr>
                    <th>Metric</th>
                    <th>Central base</th>
                    <th>Scenario base</th>
                    <th>Adjusted</th>
                    <th>Change vs base</th>
                    <th>Driver</th>
                  </tr>
                </thead>
                <tbody>
                  {baseForecastRows.map((row) => (
                    <tr key={row.metric}>
                      <td>
                        <strong>{row.metric}</strong>
                      </td>
                      <td>{formatMillions(row.base)}</td>
                      <td>{formatMillions(row.scenario)}</td>
                      <td>{formatMillions(row.adjusted)}</td>
                      <td>{formatMillions(row.adjusted - row.base)}</td>
                      <td>{row.note}</td>
                    </tr>
                  ))}
                </tbody>
                  </table>
                </div>
              </div>
            </details>
            <div className="forecast-subpanel" id="baseline-review-decision" tabIndex="-1" aria-live="polite">
              <div className="forecast-panel-head">
                <div>
                  <span className="forecast-section-code">Baseline review decision</span>
                  <h3>Confirm the inherited baseline or flag a source-data issue</h3>
                </div>
                <ForecastReadinessBadge
                  status={workspaceState.baselineDecision === 'confirmed' ? WORKSPACE_READINESS.READY
                    : workspaceState.baselineDecision === 'flagged' ? WORKSPACE_READINESS.NEEDS_ATTENTION
                      : WORKSPACE_READINESS.IN_PROGRESS}
                  issueCount={workspaceState.baselineDecision === 'flagged' ? 1 : 0}
                />
              </div>
              <label className="forecast-submit-narrative" htmlFor="baseline-data-issue">
                <span>Data issue details <small>(required only when flagging)</small></span>
                <textarea
                  id="baseline-data-issue"
                  rows={2}
                  value={workspaceState.baselineIssue || ''}
                  disabled={!canEditSubmission}
                  placeholder="Describe the stale source, missing actuals, or material exception…"
                  onChange={(event) => setWorkspaceState((previous) => ({ ...previous, baselineIssue: event.target.value }))}
                />
              </label>
              <div className="workspace-action-row">
                <button
                  type="button"
                  className="forecast-primary-button"
                  aria-pressed={workspaceState.baselineDecision === 'confirmed'}
                  disabled={!canEditSubmission}
                  onClick={() => setWorkspaceState((previous) => ({
                    ...previous, baselineDecision: 'confirmed', baselineReviewedAt: new Date().toISOString(), baselineIssue: ''
                  }))}
                >
                  Confirm baseline
                </button>
                <button
                  type="button"
                  className="forecast-secondary-button"
                  aria-pressed={workspaceState.baselineDecision === 'flagged'}
                  disabled={!canEditSubmission || !(workspaceState.baselineIssue || '').trim()}
                  onClick={() => setWorkspaceState((previous) => ({
                    ...previous, baselineDecision: 'flagged', baselineReviewedAt: new Date().toISOString()
                  }))}
                >
                  Flag data issue
                </button>
              </div>
            </div>
          </div>
        </details>
      </section>}
    </>
  );
};

export default RegionalBaselineStage;
